import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { FiImageSize, ObjectTypes } from '@fi-shared/types';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { AppTextService } from '@fi-shared/app-text';
import { isNullOrUndefined } from 'util';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IInsertMediaResult, IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { IMediaBankFile, IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiTooltipButtonIconColors } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import {
    ICheckpoint2AnswerModel,
    ICheckpointCondition,
    ICheckpointUnitSettingModel,
    ICheckpointV2,
} from '@fi-shared/checklist/checklist-v2';
import {
    Checkpoint2StatusType,
    CheckpointConditionTargetType,
    CheckpointType,
} from '@fi-shared/checklist/checklist-enum';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { IMediaBankFilesCreationDateCheckData } from '@fi-shared/media-bank/media-bank';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiExifHelperService } from '@fi-shared/fi-insert-media/fi-exif-helper.service';

@Component({
    selector: 'fi-checklist2-answer',
    templateUrl: './answer.component.html',
    styleUrls: ['./answer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-answer' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklist2AnswerComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklist2AnswerComponent,
        },
    ],
    standalone: false,
})
export class FiChecklist2AnswerComponent
    extends ControlValueAccessorValidatorBase<ICheckpoint2AnswerModel>
    implements OnInit
{
    @Input() checkpoint: ICheckpointV2;
    @Input() periodId: number;
    @Input() isQRCodeEnabled: boolean;
    @Input() checkpointUnitSetting: ICheckpointUnitSettingModel;
    @Input() viewerDisabled: boolean;
    @Input({ required: true }) auditMode: boolean;
    @Input({ required: true }) hasAccessToSchedule: boolean;

    @Output() invalidMediaBankFileIdsChange = new EventEmitter<number[]>();

    @HostBinding('class.preview-mode') @Input() isPreviewMode: boolean;
    actions: IItemsListAction2[] = [];

    CheckpointType = CheckpointType;
    Checkpoint2StatusType = Checkpoint2StatusType;
    FiImageSize = FiImageSize;
    private previousAnswerValue: number;

    get checkpointAnswer(): ICheckpoint2AnswerModel {
        return this.ngModel;
    }
    get comment(): ICommentModel {
        return this.checkpointAnswer.Comments[0];
    }

    constructor(
        private injector: Injector,
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private fiInsertMedia2Service: FiInsertMedia2Service,
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private fiDialogsService: FiDialogsService,
        private fiExifHelperService: FiExifHelperService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.setActions();
        this.previousAnswerValue = this.ngModel.Value;
    }

    private setActions() {
        this.actions = [
            {
                title: this.appTextService.get('checklist.clear_checkpoint'),
                onClick: () => this.clearCheckpoint(),
                condition: () => this.isCheckpointAnswered() && !this.viewerDisabled,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fas fa-eraser',
            },
            {
                title: this.appTextService.get('checklist.not_applicable'),
                onClick: () => this.onCheckpointNotApplicableClick(),
                condition: () =>
                    this.isChecklistShareRights() &&
                    this.hasAccessToSchedule &&
                    this.fiChecklistHelperService.canChangeCheckpointNotApplicable(this.viewerDisabled, this.auditMode),
                optionType: FiItemsListOptionType.Checkbox,
                isChecked: () => this.checkpointUnitSetting.IsNotApplicable,
            },
        ];
    }

    isAnswerDataVisible() {
        return true;
        //return (!this.viewerDisabled && !this.checkpointUnitSetting.IsNotApplicable) || this.isPreviewMode;
    }

    isStatusCheckpointType(): boolean {
        return this.checkpoint.Type === CheckpointType.Status;
    }

    getCommentAttachmentHintLabel(): string {
        if (this.isCommentMandatory() && this.isAttachmentMandatory())
            return this.appTextService.get('mobile_app.checklist_comment_and_attachment_mandatory');
        if (this.isAttachmentMandatory()) return this.appTextService.get('mobile_app.checklist_attachment_mandatory');
        if (this.isCommentMandatory()) return this.appTextService.get('mobile_app.checklist_comment_mandatory');

        return '';
    }

    isCheckpointAnswered(): boolean {
        return !isNullOrUndefined(this.checkpointAnswer.Value);
    }

    onCheckpointNotApplicableClick() {
        this.checkpointUnitSetting.IsNotApplicable = !this.checkpointUnitSetting.IsNotApplicable;
        this.onAnswerPropsChanged();
    }

    isChecklistShareRights(): boolean {
        return this.fiChecklist2Service.isChecklistShareRights(this.auditMode);
    }

    isCheckpointMenuAvailable(): boolean {
        const res =
            !this.isPreviewMode /*&& !this.viewerDisabled */ &&
            (this.isChecklistShareRights() || this.isCheckpointAnswered());
        return res;
    }

    clearCheckpoint() {
        this.fiChecklistHelperService.clearCheckpoint(this.checkpointAnswer);
        this.checkAttachmentsOnAnswerValueChanged();
    }

    openInsertMediaDialog(filesToUpload?: File[]) {
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.Checkpoint2Answer, {
                filesToUpload: filesToUpload,
                onFilesSelecting: (files) => {
                    // file was attached from computer
                    return this.validateAttachedFilesByDate(files);
                },
                onMediaBankFilesInserting: (attachments: IInsertMediaUploadItem[]): Promise<boolean> => {
                    // if 'file' property is empty - file was inserted from media library
                    const mediaBankFiles = attachments
                        .filter((attachment) => !attachment.file)
                        .map((attachment) => attachment.mediaBankFile);
                    return this.validateMediaBankFilesByDate<IMediaBankFile>(mediaBankFiles);
                },
            })
            .onYesButtonClick((result: IInsertMediaResult) => {
                this.checkpointAnswer.MediaBankFileReferences = this.checkpointAnswer.MediaBankFileReferences.concat(
                    result.mediaBankFileReferences,
                );
                this.cdr.markForCheck();
                this.onAnswerPropsChanged();
            });
    }

    private validateMediaBankFilesByDate<T extends IMediaBankFilesCreationDateCheckData>(
        mediaBankFiles: T[],
        skipErrorDialog?: boolean,
    ): Promise<boolean> {
        if (this.auditMode) return Promise.resolve(true);

        if (!this.isAttachmentMandatory()) return Promise.resolve(true);

        return this.fiExifHelperService.checkMediaBankFilesCreationDate<T>(
            mediaBankFiles,
            this.customerDataService.checklistAttachmentOldTimeIntervalMs,
            skipErrorDialog,
        );
    }

    private validateAttachedFilesByDate(files: ArrayLike<File>): Promise<boolean> {
        if (this.auditMode) return Promise.resolve(true);

        if (!this.isAttachmentMandatory()) return Promise.resolve(true);

        return this.fiExifHelperService.checkFileImagesCreationDate(
            files,
            this.customerDataService.checklistAttachmentOldTimeIntervalMs,
        );
    }

    onAnswerPropsChanged() {
        this.doPropagateChanges();
    }

    onMeasureAnswerValueChanged() {
        this.doPropagateChanges();
    }

    onMeasureAnswerValueFocusLose() {
        this.checkAttachmentsOnAnswerValueChanged();
    }

    checkAttachmentsOnAnswerValueChanged() {
        const promise = new Promise<void>((resolve, reject) => {
            this.isExistingMediaBankFileReferencesValid().then((result) => {
                if (result.isValid) {
                    resolve();
                } else {
                    const attachmentNames = this.checkpointAnswer.MediaBankFileReferences.map(
                        (mbRef) => mbRef.Name + mbRef.Extension,
                    );
                    const attachmentNamesStr = attachmentNames.join(', ');
                    this.fiDialogsService
                        .confirm({
                            headerText: this.appTextService.get('checklist.invalid_attachment_header'),
                            messageText: this.appTextService.get('checklist.invalid_attachment_message', {
                                '{attachment_name}': attachmentNamesStr,
                            }),
                            yesButtonText: this.appTextService.get('common.confirm'),
                            isTopCloseButtonShown: true,
                            headerIconClass: 'far fa-exclamation-circle red',
                            config: {
                                panelClass: 'fi-rounded-dialog-panel',
                                autoFocus: false,
                                restoreFocus: false,
                            },
                            colorClass: 'red',
                        })
                        .onYesButtonClick(() => {
                            this.invalidMediaBankFileIdsChange.emit(
                                this.checkpointAnswer.MediaBankFileReferences.map((mbRef) => mbRef.MediaBankFileId),
                            );
                            this.checkpointAnswer.MediaBankFileReferences = [];
                            resolve();
                        })
                        .onNoButtonClick(() => {
                            this.checkpointAnswer.Value = this.previousAnswerValue;
                            resolve();
                        });
                }
            }, reject);
        });

        promise.then(() => {
            this.previousAnswerValue = this.checkpointAnswer.Value;
            this.cdr.markForCheck();
            this.doPropagateChanges();
        });
    }

    private isExistingMediaBankFileReferencesValid(): Promise<{ isValid: boolean }> {
        return new Promise<{ isValid: boolean }>((resolve, reject) => {
            if (!this.checkpointAnswer.MediaBankFileReferences?.length) {
                resolve({ isValid: true });
                return;
            }

            this.validateMediaBankFilesByDate<IMediaBankFileReference>(
                this.checkpointAnswer.MediaBankFileReferences,
                true,
            ).then((isValid: boolean) => {
                resolve({ isValid: isValid });
            }, reject);
        });
    }

    isCommentMandatory(): boolean {
        return this.fiChecklistHelperService.isCommentMandatory(
            this.checkpointAnswer.Value,
            this.checkpoint.Ranges,
            this.checkpoint.Conditions,
        );
    }

    isAttachmentMandatory(): boolean {
        return this.fiChecklistHelperService.isAttachmentMandatory(
            this.checkpointAnswer.Value,
            this.checkpoint.Ranges,
            this.checkpoint.Conditions,
        );
    }

    isCommentAttachmentInvalid(): boolean {
        const commentSectionValidation: { [p: string]: boolean } = this.getCommentAttachmentValidation();
        return Object.keys(commentSectionValidation).some((key: string) => !!commentSectionValidation[key]);
    }

    private getCommentAttachmentValidation(): { [p: string]: boolean } {
        // https://chainformation.atlassian.net/browse/FI-5337
        // any values are valid for not applicable checkpoints
        if (this.checkpointUnitSetting.IsNotApplicable) return {};
        return {
            comment_mandatory:
                this.isCommentMandatory() && this.fiChecklistHelperService.isCommentEmpty(this.checkpointAnswer),
            attachment_mandatory: this.isAnswerAttachmentInvalid(),
        };
    }

    isAnswerAttachmentInvalid(): boolean {
        return this.isAttachmentMandatory() && this.fiChecklistHelperService.isAttachmentsEmpty(this.checkpointAnswer);
    }

    isQRCodeScanMandatory() {
        return this.isQRCodeEnabled && !!this.getQRCodeConditions().length;
    }

    private getQRCodeConditions(): ICheckpointCondition[] {
        const qrCodeConditions = this.checkpoint.Conditions.filter(
            (condition) => condition.TargetType === CheckpointConditionTargetType.QRCode,
        );
        return qrCodeConditions;
    }

    getMeasureUnitsText(): string {
        return this.fiCheckpoint2HelperService.getCheckpointMeasureUnitShortText(this.checkpoint.MeasureUnitType);
    }

    isAnswerDisabled() {
        return (
            this.checkpointUnitSetting.IsNotApplicable ||
            this.viewerDisabled ||
            this.isQRCodeScanMandatory() ||
            this.isPreviewMode
        );
    }

    isCommentAttachmentDisabled() {
        return (
            this.viewerDisabled || this.isQRCodeScanMandatory() || !this.isCheckpointAnswered() || this.isPreviewMode
        );
    }

    isAttachmentsButtonShown(): boolean {
        return !this.isPreviewMode;
    }

    doValidate(c: AbstractControl): ValidationErrors {
        const errors: ValidationErrors = this.getCommentAttachmentValidation() ?? {};
        errors['empty_answer_with_comment_attachments'] =
            this.fiChecklistHelperService.isEmptyAnswerWithCommentAttachments(this.checkpointAnswer);
        return errors;
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    protected readonly FiProfileSizes = FiProfileSizes;
}
